import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { purchaseAPI } from '../api';
import qrImage from '../assets/qr.jpeg';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);

  const cartTotal = getCartTotal();
  const walletBalance = user?.wallet?.depositWallet || 0;
  const repurchaseWalletBalance = user?.wallet?.repurchaseWallet || 0;

  const canPayWithWallet = walletBalance >= (discountInfo?.finalAmount || cartTotal);
  const canPayWithRepurchaseWallet = repurchaseWalletBalance >= (discountInfo?.finalAmount || cartTotal);

  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
  }, [refreshUser]);

  const calculateDiscount = useCallback(async () => {
    try {
      // Check if user has previous purchases
      const response = await purchaseAPI.getMyPurchases({ limit: 1 });
      const existingPurchases = response?.data?.purchases || [];
      const isFirstPurchase = existingPurchases.length === 0;

      let discountPercentage = 0;
      let discountAmount = 0;
      let finalAmount = cartTotal;

      if (isFirstPurchase) {
        discountPercentage = 5; // 5% discount for first purchase
        discountAmount = cartTotal * 0.05;
        finalAmount = cartTotal - discountAmount;
      } else {
        discountPercentage = 10; // 10% discount for subsequent purchases
        discountAmount = cartTotal * 0.10;
        finalAmount = cartTotal - discountAmount;
      }

      setDiscountInfo({
        originalAmount: cartTotal,
        discountPercentage,
        discountAmount,
        finalAmount,
        isFirstPurchase
      });
    } catch (err) {
      console.error('Error calculating discount:', err);
      setDiscountInfo({
        originalAmount: cartTotal,
        discountPercentage: 0,
        discountAmount: 0,
        finalAmount: cartTotal,
        isFirstPurchase: false
      });
    }
  }, [cartTotal]);

  // Calculate discount on component mount and when cart changes
  useEffect(() => {
    calculateDiscount();
  }, [cart, user, calculateDiscount]);

  // Update payment method when discount info changes
  useEffect(() => {
    if (discountInfo) {
      const finalAmount = discountInfo.finalAmount || cartTotal;
      const newPaymentMethod = canPayWithWallet ? 'WALLET' : 
                                  canPayWithRepurchaseWallet ? 'REPURCHASE_WALLET' : 'BANK_TRANSFER';
      setFormData(prev => ({ ...prev, paymentMethod: newPaymentMethod }));
    }
  }, [discountInfo?.finalAmount, walletBalance, repurchaseWalletBalance, cartTotal]);

  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    paymentMethod: 'BANK_TRANSFER',
  });

  // Payment proof states for Bank Transfer
  const [paymentProof, setPaymentProof] = useState({
    upiId: '',
    transactionId: '',
  });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="card bg-white rounded-lg shadow-md p-12 text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add items to your cart before checkout.</p>
          <button
            onClick={() => navigate('/products')}
            className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-semibold"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePaymentProofChange = (e) => {
    const { name, value } = e.target;
    setPaymentProof((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setScreenshotFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file only.');
      setScreenshotFile(null);
      return;
    }

    setError('');
    setScreenshotFile(file);
  };

  const uploadScreenshotToCloudinary = async (file) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dkhwthjov';
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'shreebakers';

    if (!cloudName || !uploadPreset) {
      throw new Error('Cloudinary environment variables are missing');
    }

    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', uploadPreset);

    const response = await fetch(`https://api.cloudinary.com/v1\_1/${cloudName}/image/upload`, {
      method: 'POST',
      body: data
    });

    const uploaded = await response.json();

    if (!response.ok || !uploaded?.secure_url) {
      throw new Error(uploaded?.error?.message || 'Cloudinary upload failed');
    }

    return {
      screenshotUrl: uploaded.secure_url,
      screenshotPublicId: uploaded.public_id || null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.address || !formData.city || !formData.state || !formData.pincode) {
      setError('Please fill in all shipping details');
      return;
    }

    // Minimum purchase check ₹310 - check ORIGINAL amount before discount
    const originalAmount = discountInfo?.originalAmount || cartTotal;
    if (originalAmount < 310) {
      setError(`Minimum purchase amount is ₹310. Please add items worth Rs. ${310 - originalAmount} more.`);
      return;
    }

    // Additional validation for Bank Transfer
    if (formData.paymentMethod === 'BANK_TRANSFER') {
      if (!paymentProof.upiId.trim()) {
        setError('UPI ID is required for Bank Transfer');
        return;
      }
      if (!screenshotFile) {
        setError('Payment screenshot is required for Bank Transfer');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      // Create base purchase data
      const purchaseData = {
        products: cart.map((item) => ({
          productId: item._id,
          quantity: item.quantity,
          price: item.price,
        })),
        shippingAddress: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        },
        totalAmount: getCartTotal(),
        paymentMethod: formData.paymentMethod,
      };

      // Handle different payment methods
      if (formData.paymentMethod === 'BANK_TRANSFER') {
        // Upload screenshot and create purchase with payment proof
        setUploading(true);
        const uploaded = await uploadScreenshotToCloudinary(screenshotFile);

        purchaseData.paymentProof = {
          upiId: paymentProof.upiId.trim(),
          transactionId: paymentProof.transactionId ? paymentProof.transactionId.trim() : '',
          screenshotUrl: uploaded.screenshotUrl,
          screenshotPublicId: uploaded.screenshotPublicId
        };

        await purchaseAPI.createPurchaseWithPaymentProof(purchaseData);

        alert('Purchase submitted successfully! Your order is pending admin verification. You will receive commission after payment verification.');
      } else if (formData.paymentMethod === 'REPURCHASE_WALLET') {
        // Handle repurchase wallet payment
        await purchaseAPI.createPurchase(purchaseData);
        alert('Purchase successful using repurchase wallet! Your order has been placed and commission distributed.');
      } else {
        // Handle deposit wallet payment (existing logic)
        await purchaseAPI.createPurchase(purchaseData);
        alert('Purchase successful! Your order has been placed.');
      }

      // Clear cart on successful purchase
      clearCart();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to place order');
      console.error('Purchase error:', err);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary mb-2">Checkout</h1>
        <p className="text-gray-500">Complete your purchase</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Checkout Form */}
        <div className="lg:col-span-2">
          <div className="card bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-secondary mb-6">Shipping Address</h2>

            {error && (
              <div className="alert alert-error mb-6">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Street address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PIN Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="BANK_TRANSFER"
                      checked={formData.paymentMethod === 'BANK_TRANSFER'}
                      onChange={handleInputChange}
                    />
                    <span className="font-semibold text-gray-700">Bank Transfer</span>
                  </label>
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${canPayWithWallet ? 'border-gray-300' : 'border-gray-200 bg-gray-50 opacity-50'
                    }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="WALLET"
                      checked={formData.paymentMethod === 'WALLET'}
                      onChange={handleInputChange}
                      disabled={!canPayWithWallet}
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-700">Deposit Wallet</span>
                      <div className="text-xs text-gray-500 mt-1">
                        Balance: Rs. {walletBalance.toLocaleString()}
                      </div>
                      {!canPayWithWallet && (
                        <div className="text-xs text-red-600 mt-1">
                          Insufficient balance (need Rs. {((discountInfo?.finalAmount || cartTotal) - walletBalance).toLocaleString()} more)
                        </div>
                      )}
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${canPayWithRepurchaseWallet ? 'border-gray-300' : 'border-gray-200 bg-gray-50 opacity-50'
                    }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="REPURCHASE_WALLET"
                      checked={formData.paymentMethod === 'REPURCHASE_WALLET'}
                      onChange={handleInputChange}
                      disabled={!canPayWithRepurchaseWallet}
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-700">Repurchase Wallet</span>
                      <div className="text-xs text-gray-500 mt-1">
                        Balance: Rs. {repurchaseWalletBalance.toLocaleString()}
                      </div>
                      {!canPayWithRepurchaseWallet && (
                        <div className="text-xs text-red-600 mt-1">
                          Insufficient balance (need Rs. {((discountInfo?.finalAmount || cartTotal) - repurchaseWalletBalance).toLocaleString()} more)
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Bank Transfer Payment Proof Section */}
              {formData.paymentMethod === 'BANK_TRANSFER' && (
                <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-secondary mb-4">Bank Transfer Payment Details</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* QR Code Display */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Scan QR Code to Pay</h4>
                      <div className="border rounded-lg p-6 flex items-center justify-center bg-white">
                        <img src={qrImage} alt="Payment QR Code" className="max-w-full h-auto max-h-64 rounded-md" />
                      </div>
                      <p className="text-xs text-gray-600 mt-2 text-center">
                        Scan this QR code with any UPI app to make the payment
                      </p>
                    </div>

                    {/* Payment Proof Form */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">UPI ID Used for Payment *</label>
                        <input
                          type="text"
                          name="upiId"
                          value={paymentProof.upiId}
                          onChange={handlePaymentProofChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="example@upi"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Transaction ID (Optional)</label>
                        <input
                          type="text"
                          name="transactionId"
                          value={paymentProof.transactionId}
                          onChange={handlePaymentProofChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter UPI transaction ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Screenshot *</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          required
                        />
                        {screenshotFile && (
                          <p className="mt-1 text-xs text-gray-500">Selected: {screenshotFile.name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-xs text-yellow-800 font-semibold">
                      ⚠️ Important: After making the payment, please upload the screenshot above.
                      Your order will be processed only after admin verifies the payment proof.
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-6 border-t border-gray-200 flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="flex-1 px-6 py-3 bg-gray-200 text-secondary rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Back to Cart
                </button>
                <button
                  type="submit"
                  disabled={loading || uploading}
                  className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 transition-colors font-bold"
                >
                  {loading || uploading ? 'Processing...' : 'Place Order'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="card bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h3 className="text-xl font-bold text-secondary mb-6">Order Summary</h3>

            <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
              {cart.map((item) => (
                <div key={item._id} className="flex justify-between text-sm">
              <div className="text-gray-700">
                <div className="font-semibold">{item.name}</div>
                <div className="text-xs text-gray-500">Qty: {item.quantity}</div>
              </div>
              <div className="font-semibold text-primary">
                Rs. {(item.price * item.quantity).toLocaleString()}
              </div>
            </div>
              ))}
          </div>

          <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
            <div className="flex justify-between text-gray-700">
              <span>Subtotal:</span>
              <span className="font-semibold">Rs. {getCartTotal().toLocaleString()}</span>
            </div>
            {discountInfo && discountInfo.discountPercentage > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({discountInfo.discountPercentage}% {discountInfo.isFirstPurchase ? '- First Purchase!' : '- Loyalty Discount!'}):</span>
                <span className="font-semibold">-Rs. {discountInfo.discountAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-700">
              <span>Shipping:</span>
              <span className="font-semibold text-accent">Free</span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>Tax:</span>
              <span className="font-semibold">Rs. 0</span>
            </div>
          </div>

          <div className="flex justify-between items-center mb-8">
            <span className="font-bold text-secondary">Total Amount:</span>
            <div className="text-right">
              {discountInfo && discountInfo.discountPercentage > 0 && (
                <div className="text-sm text-gray-500 line-through">
                  Rs. {discountInfo.originalAmount.toLocaleString()}
                </div>
              )}
              <span className="text-2xl font-bold text-primary">Rs. {(discountInfo?.finalAmount || getCartTotal()).toLocaleString()}</span>
            </div>
          </div>

          {(discountInfo?.finalAmount || getCartTotal()) < 279 && (
            <div className="text-sm text-red-600 font-semibold">
              Minimum order value is ₹279. Please add more items.
            </div>
          )}

          {discountInfo && discountInfo.discountPercentage > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
              <p className="text-xs text-green-800 font-semibold">
                🎉 {discountInfo.isFirstPurchase ? 'First Purchase Discount Applied!' : 'Loyalty Discount Applied!'}
              </p>
              <p className="text-xs text-green-800 mt-1">
                You saved Rs. {discountInfo.discountAmount.toLocaleString()} on this order!
              </p>
            </div>
          )}

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-800 font-semibold mb-2">✓ You will earn commission</p>
            <p className="text-xs text-green-800">On successful order completion, you'll receive commission based on your rank and purchase amount.</p>
          </div>
        </div>
      </div>
    </div>
    </div >
  );
};

export default Checkout;